import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Tab } from '@headlessui/react';
import InventoryList from '../components/warehouse/InventoryList';
import InventoryMovementsList from '../components/warehouse/InventoryMovementsList';
import InventoryDocuments from '../components/warehouse/InventoryDocuments';
import StockTransferForm from '../components/warehouse/StockTransferForm';
import StockReceiptForm from '../components/warehouse/StockReceiptForm';
import { ArrowsRightLeftIcon, TruckIcon, ArchiveBoxIcon } from '@heroicons/react/24/outline';
import CommonPageHeader from '../components/common/CommonPageHeader';

const Inventory: React.FC = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const productId = searchParams.get('productId');
  const [isStockTransferOpen, setIsStockTransferOpen] = useState(false);
  const [isStockReceiptOpen, setIsStockReceiptOpen] = useState(false);

  const handleTransferSuccess = () => {
    // Refresh inventory data - this will be handled by the InventoryList component
  };

  const handleReceiptSuccess = () => {
    // Refresh inventory data - this will be handled by the InventoryList component
  };

  const handleMovementsRefresh = () => {
    // This can be used for additional refresh logic if needed
    console.log('Inventory movements refreshed');
  };

  return (
    <div className="w-full max-w-full flex-grow space-y-4">
      <CommonPageHeader
        title={t('inventory.title')}
        subtitle={t('inventory.subtitle')}
        icon={ArchiveBoxIcon}
      >
        {productId && (
          <div className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-white/20 backdrop-blur-sm text-white border border-white/30">
            {t('inventory.filteredByProduct')}: {productId}
          </div>
        )}
        
        <button
          onClick={() => setIsStockTransferOpen(true)}
          className="group relative inline-flex items-center justify-center px-4 py-2 border border-white/30 text-sm font-medium rounded-lg shadow-sm text-white bg-white/20 backdrop-blur-sm hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white/50 transition-all duration-200"
        >
          <ArrowsRightLeftIcon className="h-4 w-4 mr-2 group-hover:rotate-180 transition-transform duration-300" />
          <span>{t('inventory.actions.transfer')}</span>
        </button>
        
        <button
          onClick={() => setIsStockReceiptOpen(true)}
          className="group relative inline-flex items-center justify-center px-4 py-2 border border-white/30 text-sm font-medium rounded-lg shadow-sm text-white bg-white/20 backdrop-blur-sm hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white/50 transition-all duration-200"
        >
          <TruckIcon className="h-4 w-4 mr-2 group-hover:translate-x-1 transition-transform duration-300" />
          <span>{t('inventory.actions.stockIn')}</span>
        </button>
      </CommonPageHeader>

      <div className="bg-white dark:bg-gray-800 rounded-lg sm:rounded-xl shadow-sm w-full mx-2 sm:mx-0">
        <Tab.Group>
          <Tab.List className="flex border-b border-gray-200 dark:border-gray-700 w-full">
            <Tab
              className={({ selected }) =>
                `py-3 px-4 sm:px-6 text-sm font-medium outline-none flex-1 sm:flex-none text-center ${
                  selected
                    ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-500 dark:border-primary-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`
              }
            >
              {t('inventory.tab.stock')}
            </Tab>
            <Tab
              className={({ selected }) =>
                `py-3 px-4 sm:px-6 text-sm font-medium outline-none flex-1 sm:flex-none text-center ${
                  selected
                    ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-500 dark:border-primary-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`
              }
            >
              {t('inventory.tab.movements')}
            </Tab>
            <Tab
              className={({ selected }) =>
                `py-3 px-4 sm:px-6 text-sm font-medium outline-none flex-1 sm:flex-none text-center ${
                  selected
                    ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-500 dark:border-primary-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`
              }
            >
              {t('inventory.tab.documents')}
            </Tab>
          </Tab.List>
          <Tab.Panels className="p-3 sm:p-6 w-full">
            <Tab.Panel className="w-full">
              <InventoryList productId={productId || undefined} />
            </Tab.Panel>
            <Tab.Panel className="w-full">
              <InventoryMovementsList productId={productId || undefined} limit={100} onRefresh={handleMovementsRefresh} />
            </Tab.Panel>
            <Tab.Panel className="w-full">
              <InventoryDocuments productId={productId || undefined} />
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      </div>
      
      <StockTransferForm
        isOpen={isStockTransferOpen}
        onClose={() => setIsStockTransferOpen(false)}
        onSuccess={handleTransferSuccess}
      />
      
      <StockReceiptForm
        isOpen={isStockReceiptOpen}
        onClose={() => setIsStockReceiptOpen(false)}
        onSuccess={handleReceiptSuccess}
      />
    </div>
  );
};

export default Inventory; 